import {useMemo, useState} from "react";
import {Radio, Tabs, TextInput} from "@mantine/core";
import {useTranslation} from "react-i18next";
import {useLocation, useNavigate} from "react-router-dom";
import {notifications} from "@mantine/notifications";
import type {CollectionDataResponse, LabelGroup} from "../../types";
import {dibAppApi} from "../../../../dibAppApi/dibAppApi";
import {useContentParams} from "../../router";
import {ChecklistModal} from "../ChecklistModal";
import {ChecklistModalActions} from "../ChecklistModalActions";
import {buildChecklistDocHref} from "../checklistRefUrl";
import {buildCollectionUrl, type TopicMeta} from "./utils";
import {OpenFillingModal} from "./OpenFillingModal";
import classes from "../modalStyles.module.css";

type FillingFormModalProps = {
    opened: boolean;
    onClose: () => void;
    mode: "create" | "edit" | "open";
    topicMeta: TopicMeta;
    defaultCollectionId: string;
    viewGroups: LabelGroup[];
    labelData: Record<string, string>;
    collectionData?: CollectionDataResponse;
    onPatchDimensions: (dimensions: string[]) => Promise<void>;
    onPatchCollectionName: (name: string) => Promise<void>;
    isPatchCollectionNameLoading: boolean;
    isPatchDimensionsLoading: boolean;
    onCollectionChange: () => void;
};

const tabsClassNames = {
    list: classes.modalTabsList,
    tab: classes.modalTab,
    panel: classes.modalTabsPanel,
};

const rowClassNames = {
    root: classes.modalRow,
    labelWrapper: classes.modalRowLabelWrapper,
    label: classes.modalRowLabel,
};

export const FillingFormModal = ({
    opened,
    onClose,
    mode,
    topicMeta,
    defaultCollectionId,
    viewGroups,
    labelData,
    collectionData,
    onPatchDimensions,
    onPatchCollectionName,
    isPatchCollectionNameLoading,
    isPatchDimensionsLoading,
    onCollectionChange,
}: FillingFormModalProps) => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const isNew = mode === "create" || !collectionData;

    const defaultTab = mode === "open" ? "open" : "form";

    // A new filling starts from the dimensions that the view shows now.
    // An existing filling starts from its own labels.
    const initialDimensions = useMemo(() => {
        const dims: Record<string, string> = {};
        const selectedLabelIds = isNew
            ? []
            : (collectionData?.labels?.map((l) => l.labelId) ?? []);

        for (const group of viewGroups) {
            const match = selectedLabelIds.find((labelId) =>
                group.labels.some((l) => l.id === labelId),
            );
            dims[group.id] =
                match ?? labelData[group.id] ?? group.labels[0]?.id ?? "";
        }
        return dims;
    }, [isNew, viewGroups, collectionData, labelData]);

    const [name, setName] = useState(
        isNew ? "" : (collectionData?.metadata?.name ?? ""),
    );
    const [dimensions, setDimensions] = useState(initialDimensions);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [createCollection] =
        dibAppApi.endpoints.createCollection.useMutation();

    // A filling created or renamed while a template is open on the Maler tab
    // stays on that template (legacy `redirectToCollection`). The open
    // template is the named route param `/dokument/:dokumentName/:dokumentId`;
    // dropping it from the URL is what switches the checklist to the
    // Sjekkliste tab, since the active tab is derived from `dokumentId`.
    const {dokumentName, dokumentId} = useContentParams();
    const buildFillingUrl = (collection: {name: string; id: string}) =>
        dokumentName && dokumentId
            ? buildChecklistDocHref(
                  {name: dokumentName, topicId: dokumentId},
                  buildCollectionUrl(topicMeta),
                  {
                      metadata: {
                          collectionId: collection.id,
                          name: collection.name,
                      },
                  },
              )
            : buildCollectionUrl(topicMeta, collection);

    // The slug in the address bar contains the old name. Replace the URL when
    // the user looks at the collection that was renamed.
    const navigateToRenamedCollection = (newName: string) => {
        const collectionId = collectionData?.metadata?.collectionId;
        if (!collectionId) return;

        const path = location.pathname.toLowerCase();
        if (!path.includes(collectionId.toLowerCase())) return;

        navigate(
            buildFillingUrl({
                name: newName,
                id: collectionId.toLowerCase(),
            }),
            {replace: true},
        );
    };

    const handleSubmit = async () => {
        if (!name.trim()) return;

        setIsSubmitting(true);

        const changedDims = Object.values(dimensions).filter(
            (id) => id && !Object.values(initialDimensions).includes(id),
        );

        try {
            if (isNew) {
                // The server merges the list over the topic defaults.
                // Send every group to keep the selection that the view shows.
                const result = await createCollection({
                    topicId: topicMeta.topicId,
                    name: name.trim(),
                    dimensions: Object.values(dimensions).filter(Boolean),
                }).unwrap();

                onCollectionChange();
                navigate(
                    buildFillingUrl({
                        name: result.name,
                        id: result.collectionId.toLowerCase(),
                    }),
                );
                onClose();
            } else {
                if (name.trim() !== collectionData?.metadata?.name) {
                    await onPatchCollectionName(name.trim());
                    navigateToRenamedCollection(name.trim());
                }
                if (changedDims.length > 0) {
                    await onPatchDimensions(changedDims);
                }

                onClose();
            }
        } catch {
            notifications.show({
                message: t("Error.SomethingWentWrong"),
                color: "red",
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const loading =
        isSubmitting ||
        isPatchCollectionNameLoading ||
        isPatchDimensionsLoading;

    const editCreateLabel = isNew ? t("Word.New") : t("Word.Edit");

    return (
        <ChecklistModal
            opened={opened}
            onClose={onClose}
            title={t("Word.Filling")}
        >
            <Tabs defaultValue={defaultTab} classNames={tabsClassNames}>
                <Tabs.List>
                    <Tabs.Tab value="form">{editCreateLabel}</Tabs.Tab>
                    <Tabs.Tab value="open">{t("Word.Open")}</Tabs.Tab>
                </Tabs.List>

                <Tabs.Panel value="form">
                    <div className={classes.modalBody}>
                        <TextInput
                            label={
                                isNew ? t("Action.NameFilling") : t("Word.Name")
                            }
                            withAsterisk
                            value={name}
                            onChange={(e) => setName(e.currentTarget.value)}
                            autoFocus
                            classNames={{
                                root: classes.selectRoot,
                                label: classes.selectLabel,
                            }}
                        />

                        {viewGroups.map((group) => (
                            <Radio.Group
                                key={group.id}
                                label={group.name}
                                value={dimensions[group.id]}
                                onChange={(value) =>
                                    setDimensions((prev) => ({
                                        ...prev,
                                        [group.id]: value,
                                    }))
                                }
                                classNames={{label: classes.groupLabel}}
                            >
                                <div className={classes.modalRowList}>
                                    {group.labels.map((label) => (
                                        <Radio
                                            key={label.id}
                                            value={label.id}
                                            label={label.name}
                                            color="gneis.9"
                                            classNames={rowClassNames}
                                        />
                                    ))}
                                </div>
                            </Radio.Group>
                        ))}
                    </div>

                    <ChecklistModalActions
                        primary={{
                            label: isNew ? t("Word.create") : t("Action.Save"),
                            onClick: handleSubmit,
                            loading,
                            disabled: !name.trim() || loading,
                        }}
                        secondary={{
                            label: t("common.cancel"),
                            onClick: onClose,
                        }}
                    />
                </Tabs.Panel>

                <Tabs.Panel value="open">
                    <OpenFillingModal
                        onClose={onClose}
                        onCollectionChange={onCollectionChange}
                        topicId={topicMeta.topicId}
                        topicMeta={topicMeta}
                    />
                </Tabs.Panel>
            </Tabs>
        </ChecklistModal>
    );
};
